import { useEffect, useState } from 'react';
import { acrossFrom, surface } from './surface';

/**
 * A very small router.
 *
 * The dashboard has seven routes and two nesting levels. A routing library would add a
 * dependency, a bundle and an API to learn in exchange for features none of these routes use.
 *
 * It has grown one feature since: a page can refuse to be left. That is here rather than in the
 * page because this file is the only thing that sees every way out of one.
 */

/**
 * A page that must be asked before it is left.
 *
 * Exactly one at a time, and it is the Playground: leaving it while it holds a sandbox either
 * leaves that sandbox running with nobody in it or, mid-turn, ends it under the assistant — both
 * things to be asked about rather than discovered. `false` from the guard means it has taken over
 * — it will show its own dialog and call `navigate` again once the user has answered.
 *
 * It lives here rather than in the page because there is no other place that sees every way out.
 * The dashboard's links, its Back control and its programmatic jumps all funnel through
 * `navigate`, and Back/Forward arrive as `popstate`, which is handled below. A guard installed on
 * the links alone would be one the browser's own Back button walked straight past.
 *
 * Tab close and reload do not come through here at all — nothing in a single-page app can see
 * those but `beforeunload`, and the page installs one of those too, with `watchForStay` below for
 * what happens after it.
 */
type NavigationGuard = (to: string) => boolean;
let guard: NavigationGuard | null = null;
/** Where the guarded page sits, so a Back can be put back before the user is asked. */
let guardedAt = '';
/** True while `navigate()` announces a move the guard has already been asked about. */
let selfNavigating = false;

/** Returns the uninstaller, so a `useEffect` can hand it straight back. */
export function blockNavigation(next: NavigationGuard): () => void {
  guard = next;
  guardedAt = window.location.pathname + window.location.search;
  return () => {
    if (guard === next) guard = null;
  };
}

/** The guard's answer, or `true` when there is nothing installed. */
function mayLeave(to: string): boolean {
  return !guard || guard(to);
}

/** How long a page must outlive its *Leave site?* prompt before the answer is taken to be Stay. */
export const STAY_SETTLE_MS = 750;

/**
 * Find out that a *Leave site?* prompt was answered with Stay. Call it from `beforeunload`, after
 * asking for the prompt; call what it returns from `pagehide`.
 *
 * The browser's prompt is the only thing a closing tab allows, it ignores any text a page offers,
 * and nothing reports the answer. What does tell them apart is whether the page is still here
 * afterwards: a Leave is followed by `pagehide`, which cancels this, and a Stay by nothing at all,
 * so `onStay` runs — and the page can say, in its own words, what the prompt could not.
 *
 * Two timers rather than one, because in Chrome and Safari the prompt blocks the page's thread
 * while it is up. A single timer started in the handler would be overdue the instant the prompt
 * closed, whichever button closed it, and would fire before the `pagehide` of a Leave got the
 * chance to cancel it. The first timer only runs once the page's thread is free again; the settle
 * period starts there.
 *
 * Wrong in two directions, both harmless for what it is used for. A Leave to a page that is slow to
 * arrive keeps this document alive past the settle period, so `onStay` can run a moment before it
 * goes. A browser whose prompt does not block the page may run it while the prompt is still up.
 */
export function watchForStay(onStay: () => void): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined = setTimeout(() => {
    timer = setTimeout(() => {
      timer = undefined;
      onStay();
    }, STAY_SETTLE_MS);
  }, 0);
  return () => {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
  };
}

/**
 * A say over every route change the app makes itself, for a shell that may only show some pages.
 *
 * The desktop app's window is the library and nothing else. Its native side refuses a navigation
 * to anything outside the library, but `pushState` is not a navigation and is never asked — so
 * where the page is in that window, it installs this (`storefront/link/use-link.ts`) and is asked
 * instead. The hold answers with the path to go to, which may not be the one asked for, or null
 * for "nowhere": it has dealt with it.
 */
type Hold = (path: string) => string | null;
let hold: Hold | null = null;

export function holdNavigation(next: Hold | null): void {
  hold = next;
}

/**
 * Where a route change really goes when it is not a page this origin draws: another origin
 * outright, or a path that belongs to the site's other host (`surface.ts`). `null` for an ordinary
 * change of route.
 *
 * Asked before anything is pushed, because the alternative is to push the path here and let the
 * page notice it is on the wrong host: that leaves an entry behind which Back lands on, which
 * sends the browser forward again, which is a Back button that does not work.
 */
function elsewhere(path: string): string | null {
  let url: URL;
  try {
    url = new URL(path, window.location.href);
  } catch {
    return null;
  }
  if (url.origin !== window.location.origin) return url.href;
  return acrossFrom(surface(), url);
}

/**
 * `replace` rewrites the current entry instead of adding one — for a redirect the user never
 * chose, where a pushed entry would mean Back lands on the URL that just redirected.
 *
 * `force` is how a guard finishes what it interrupted: the page that blocked a navigation has
 * asked, been told to go, and is now replaying it. Without it the guard would refuse its own
 * retry.
 *
 * `scroll: false` is for an address that names something opened *over* the page rather than in
 * place of it — a game in the library — where the page underneath has to stay where it was.
 */
export function navigate(path: string, options?: { replace?: boolean; force?: boolean; scroll?: boolean }): void {
  if (hold) {
    const held = hold(path);
    if (held === null) return;
    path = held;
  }
  if (window.location.pathname + window.location.search === path) return;
  if (!options?.force && !mayLeave(path)) return;
  // A page of the other host is a document to load, not a route to render.
  const away = elsewhere(path);
  if (away !== null) {
    if (options?.replace) window.location.replace(away);
    else window.location.assign(away);
    return;
  }
  if (options?.replace) {
    window.history.replaceState({}, '', path);
  } else {
    window.history.pushState({}, '', path);
    // A pushed entry is a new page, and the browser keeps the scroll offset of the old one —
    // so a link near the bottom of a long page (the guide's "How a tester installs →") opens
    // the next one halfway down. Only for pushes: a `replace` corrects the URL of the page
    // the user is already reading, and Back/Forward arrive as real popstate events, where the
    // browser's own restoration is the behaviour people expect. A fragment addresses a
    // position within the target page, so it keeps whatever position it names.
    if (!path.includes('#') && options?.scroll !== false) window.scrollTo(0, 0);
  }
  // Flagged, because the listener below cannot otherwise tell this from a real Back — and it
  // must not put a navigation the guard has already agreed to straight back where it came from.
  selfNavigating = true;
  try {
    window.dispatchEvent(new PopStateEvent('popstate'));
  } finally {
    selfNavigating = false;
  }
}

/**
 * A Back or Forward has landed. `true` if the app should render where the URL now points.
 *
 * Back is the awkward one: it has already happened by the time anything can see it, so refusing
 * means undoing. That is a `pushState` back to where the guarded page sits, rather than
 * `history.back()` — calling that from inside a `popstate` handler queues a second event to
 * disentangle from the first, and all this has to achieve is an address bar that agrees with
 * the screen.
 *
 * Exported because it is the whole of the interesting logic and `useRoute` cannot be tested
 * without a DOM. It is not for callers.
 */
export function acceptPopState(): boolean {
  if (selfNavigating) return true;
  const to = window.location.pathname + window.location.search;
  if (mayLeave(to)) return true;
  window.history.pushState({}, '', guardedAt);
  return false;
}

/**
 * The rendered route.
 *
 * The guard is consulted *here* rather than in a listener of its own, because of exactly one
 * race: if this hook learns the new path first, it renders the destination, which unmounts the
 * guarded page, which uninstalls the guard — and the question never gets asked. Since every
 * route the dashboard renders comes from this one hook, checking before `setPath` is checking
 * before anything can act on it.
 */
export function useRoute(): string {
  const [path, setPath] = useState(() => window.location.pathname);
  useEffect(() => {
    const onChange = () => {
      if (!acceptPopState()) return;
      setPath(window.location.pathname);
    };
    window.addEventListener('popstate', onChange);
    // A child's effect runs before this one, so a redirect a page makes as it mounts (`Go` in
    // `App.tsx`) has already announced itself to nobody. The address is read again now that
    // somebody is listening; no guard can have been installed before the first route rendered.
    setPath(window.location.pathname);
    return () => window.removeEventListener('popstate', onChange);
  }, []);
  return path;
}

/**
 * Matches `/games/:gameId` style patterns, returning the captured segments or null.
 */
export function match(pattern: string, path: string): Record<string, string> | null {
  const patternParts = pattern.split('/').filter(Boolean);
  const pathParts = path.split('/').filter(Boolean);
  if (patternParts.length !== pathParts.length) return null;

  const params: Record<string, string> = {};
  for (let i = 0; i < patternParts.length; i++) {
    const expected = patternParts[i]!;
    const actual = pathParts[i]!;
    if (expected.startsWith(':')) params[expected.slice(1)] = decodeURIComponent(actual);
    else if (expected !== actual) return null;
  }
  return params;
}

/**
 * The fragment, split into the section and whatever the section is showing within itself.
 *
 * `#channels/beta` is one section with something selected inside it, not two sections. Everything
 * before the first slash names the tab; everything after it belongs to that tab and means nothing
 * to anybody else.
 */
export function splitHash(): [head: string, tail: string] {
  const raw = window.location.hash.replace(/^#/, '');
  const slash = raw.indexOf('/');
  return slash < 0 ? [raw, ''] : [raw.slice(0, slash), raw.slice(slash + 1)];
}

/**
 * Rewrite the fragment in place and tell both hooks below about it.
 *
 * `replaceState` fires no `hashchange` of its own, and these are two independent hooks reading one
 * string — so a tab switch that did not announce itself would leave a stale selection on screen
 * inside the tab that just went away.
 */
function writeHash(fragment: string) {
  window.history.replaceState({}, '', `${window.location.pathname}${fragment ? `#${fragment}` : ''}`);
  window.dispatchEvent(new Event('hashchange'));
}

/** Subscribes a component to fragment changes, however they arrive. */
function useHash<T>(read: () => T): T {
  const [value, setValue] = useState<T>(read);
  useEffect(() => {
    const onChange = () => setValue(read());
    window.addEventListener('hashchange', onChange);
    window.addEventListener('popstate', onChange);
    return () => {
      window.removeEventListener('hashchange', onChange);
      window.removeEventListener('popstate', onChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return value;
}

/**
 * The section of a page with more than one, kept in the URL fragment.
 *
 * Sections are addressable on purpose: a link to a game's beta channel is a thing people paste
 * into chat, and it has to land on the panel it names rather than on the page's default one. The
 * fragment rather than a query string, because it is a position within one page and the server
 * never needs to see it.
 */
export function useSection<T extends string>(sections: readonly T[], fallback: T): [T, (next: T) => void] {
  const section = useHash<T>(() => {
    const [head] = splitHash();
    return (sections as readonly string[]).includes(head) ? (head as T) : fallback;
  });

  return [
    section,
    (next: T) => {
      // The tail goes with the tab that owned it. `#channels/beta` selects a channel, and carrying
      // `beta` over to Settings would name something that does not exist there.
      writeHash(next);
    },
  ];
}

/**
 * The path *within* the current section — `['beta', 'testers']` of `#channels/beta/testers`.
 *
 * One section has a screen of its own inside it: Channels is a list, a channel, and a section of
 * that channel. Everything after the tab's name belongs to the tab and means nothing outside it,
 * so this hands back the segments and takes no view on what they are.
 *
 * `[]` is nothing selected, which is the section's own index. Kept out of `useSection` because
 * every other section is flat and would carry a second return value it never reads.
 *
 * `replaceState`, like the tab switch above and for the same reason: Back should leave the game
 * screen, not walk back out through every channel somebody opened on the way. The way back up is
 * the visible link the section draws, not the browser's button.
 */
export function useSubPath(): [string[], (next: string[]) => void] {
  // The joined string is what goes in state, so a fragment that has not changed does not hand
  // back a new array on every `hashchange` and re-render everything reading it.
  const tail = useHash(() => splitHash()[1]);
  const path = tail === '' ? [] : tail.split('/').map(decodeURIComponent);

  return [
    path,
    (next: string[]) => {
      const [head] = splitHash();
      writeHash([head, ...next.map(encodeURIComponent)].join('/'));
    },
  ];
}

/** An <a> that routes client-side but still behaves like a link (middle-click, ⌘-click). */
export function linkProps(href: string): {
  href: string;
  onClick: (event: React.MouseEvent) => void;
} {
  return {
    href,
    onClick: (event: React.MouseEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey) return;
      if (event.button !== 0) return;
      event.preventDefault();
      // Every press goes through `navigate`, a link to the site's other host included: it is the
      // one place that asks a page whether it may be left, and the one that knows where a path
      // that belongs to the other host is to be loaded from.
      navigate(href);
    },
  };
}
